import { WorkInstructionDto } from '@operations/app/api'
import {
  canStartWorkInstruction,
  canStopWorkInstruction,
} from '@operations/features/workInstructions/helpers/work-instruction-table.helper'
import { useTranslate } from '@tolgee/react'
import { TomTableActionsProps } from '@tom-ui/ui'
import { MRT_Row } from 'material-react-table'
import { appStore } from 'modules/utils/src/stores/AppStore'
import { useCallback } from 'react'
import { CraneSplitDialogUIStore } from '../stores/crane-split-dialog.ui-store'

export const useWorkQueueInstructionTableActions = (uiStore: CraneSplitDialogUIStore) => {
  const { t } = useTranslate()

  const setPriority = useCallback(
    async (workInstructions: WorkInstructionDto[], priority: boolean) => {
      try {
        await uiStore.setPriority(
          workInstructions.filter(wi => wi.isPriority !== priority).map(wi => wi.id),
          priority,
        )
        appStore.setShowAlert(
          'success',
          t(
            priority ? 'prioritySetToHigh' : 'prioritySetToDefault',
            priority ? 'Priority set to high' : 'Priority set to default',
          ),
        )
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t(
            priority ? 'errorSettingHighPriority' : 'errorSettingDefaultPriority',
            priority ? 'Error setting high priority' : 'Error setting default priority',
          ),
        )
      }
    },
    [t, uiStore],
  )

  const handleSetHighPriority = useCallback(
    async (workInstructions: WorkInstructionDto[]) => setPriority(workInstructions, true),
    [setPriority],
  )

  const handleSetDefaultPriority = useCallback(
    async (workInstructions: WorkInstructionDto[]) => setPriority(workInstructions, false),
    [setPriority],
  )

  const handleStop = useCallback(
    async (workInstructions: WorkInstructionDto[]) => {
      try {
        await uiStore.stopWorkInstructions(workInstructions)
        appStore.setShowAlert('success', t('workInstructionStopped', 'Work instruction stopped'))
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t('errorStoppingWorkInstruction', 'Error stopping work instruction'),
        )
      }
    },
    [t, uiStore],
  )

  const handleStart = useCallback(
    async (workInstructions: WorkInstructionDto[]) => {
      try {
        await uiStore.startWorkInstruction(workInstructions)
        appStore.setShowAlert('success', t('workInstructionStarted', 'Work instruction started'))
      } catch (error) {
        appStore.setShowAlert(
          'error',
          t('errorStartingWorkInstruction', 'Error starting work instruction'),
        )
      }
    },
    [t, uiStore],
  )

  return useCallback(
    (selectedRows: MRT_Row<WorkInstructionDto>[]): TomTableActionsProps<WorkInstructionDto>[] => {
      const workInstructions = selectedRows.map(row => row.original)

      return [
        {
          label: t('start', 'Start'),
          grouped: true,
          disabled: workInstructions.some(wi => !canStartWorkInstruction(wi.status)),
          onClick: () => handleStart(workInstructions),
        },
        {
          label: t('stop', 'Stop'),
          grouped: true,
          disabled: workInstructions.some(wi => !canStopWorkInstruction(wi.status)),
          onClick: () => handleStop(workInstructions),
        },
        {
          label: t('setHighPriority', 'Set High Priority'),
          grouped: true,
          disabled: workInstructions.every(wi => wi.isPriority),
          onClick: () => handleSetHighPriority(workInstructions),
        },
        {
          label: t('setDefaultPriority', 'Set Default Priority'),
          grouped: true,
          disabled: workInstructions.every(wi => !wi.isPriority),
          onClick: () => handleSetDefaultPriority(workInstructions),
        },
      ]
    },
    [t],
  )
}
